/**
 * Shared constants for PureWireless layers (ESRI + Google Maps).
 * Colors are RGBA tuples [r, g, b, a] with 0–255 alpha.
 */

export type RGBA = [number, number, number, number];

// ─── URLs ─────────────────────────────────────────────────────────────────────

/** MVT tile template for PureWireless building/outdoor tiles. */
export const PURE_WIRELESS_URL = 'https://911inside.com/tiles/{z}/{x}/{y}.pbf';
/** Base URL for GeoJSON site data (append /:siteId). */
export const GEOJSON_SITE_URL = 'https://911inside.com/geojson/site';

// ─── Floor / zoom ─────────────────────────────────────────────────────────────

/** Default floor height in meters when tile data has none (~10 ft). */
export const DEFAULT_FLOOR_HEIGHT_METERS = 3.0;
/** Min zoom for room labels (Google Maps). */
export const ROOM_LABEL_MIN_ZOOM = 19;
/** Min zoom for point markers (Google Maps). */
export const POINT_MARKER_MIN_ZOOM = 19;
/** Min zoom for point labels (Google Maps). */
export const POINT_LABEL_MIN_ZOOM = 20;

// ─── Point marker icon ───────────────────────────────────────────────────────

/** Triangle icon for outdoor points (#E05858 red). Used by the outdoor MVT IconLayer. */
export const POINT_MARKER_TRIANGLE_ICON = {
  url:
    'data:image/svg+xml;charset=utf-8,' +
    encodeURIComponent(
      '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">' +
        '<polygon points="16,2 30,30 2,30" fill="#E05858"/>' +
        '</svg>',
    ),
  width: 32,
  height: 32,
  anchorY: 32,
};

/** Triangle icon for indoor points (#EFEFEF light gray). Used by ESRI indoor point markers. */
export const INDOOR_POINT_MARKER_ICON = {
  url:
    'data:image/svg+xml;charset=utf-8,' +
    encodeURIComponent(
      '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">' +
        '<polygon points="16,2 30,30 2,30" fill="#EFEFEF"/>' +
        '</svg>',
    ),
  width: 32,
  height: 32,
  anchorY: 32,
};

/** Indoor point marker color [239, 239, 239, 255] (#EFEFEF). For ESRI esriTriangleMarker. */
export const INDOOR_POINT_MARKER_COLOR: RGBA = [239, 239, 239, 255];

// ─── Text ────────────────────────────────────────────────────────────────────

export const TEXT_COLOR_DARK: RGBA = [255, 255, 255, 255];
export const TEXT_COLOR_LIGHT: RGBA = [51, 51, 51, 255];
/** SDF text outline for Google Maps (deck.gl TextLayer). */
export const TEXT_OUTLINE_COLOR: RGBA = [0, 0, 0, 200];

// ─── MVT footprint outlines ─────────────────────────────────────────────────

/** Fill for MVT building footprints (#CBD5E1). */
export const MVT_SIMPLE_FILL_COLOR: RGBA = [203, 213, 225, 255];
/** Outline for MVT building footprints (#94A3B8). */
export const MVT_SIMPLE_LINE_COLOR: RGBA = [148, 163, 184, 255];

// ─── Building footprint (GeoJSON detail + MVT) ──────────────────────────────

/** Outline color for building footprints (#94A3B8). */
export const FOOTPRINT_OUTLINE_COLOR: RGBA = [148, 163, 184, 255];
/** Fill color for building footprints (#94A3B8). */
export const FOOTPRINT_FILL_COLOR: RGBA = [148, 163, 184, 255];
/** Lighter fill for GeoJSON footprint background. */
export const GEOJSON_FOOTPRINT_FILL: RGBA = [204, 204, 204, 150];
/** Default footprint fill (neutral gray) when a tile has no fill_color of its own. */
export const FOOTPRINT_DEFAULT_FILL_COLOR: RGBA = [128, 128, 128, 255];
/**
 * Default footprint border when a tile has no border_color of its own:
 * Coca-Cola brand red (#F40009). A distinct default seam so adjacent or
 * overlapping footprints read as separate buildings rather than merging.
 */
export const FOOTPRINT_DEFAULT_BORDER_COLOR: RGBA = [244, 0, 9, 255];

// ─── Room polygons ──────────────────────────────────────────────────────────

/** Outline for individual room polygons. */
export const ROOM_OUTLINE_COLOR: RGBA = [0, 0, 0, 255];

// ─── Selection / highlight ──────────────────────────────────────────────────

/** Auto-highlight color for pickable layers. */
export const HIGHLIGHT_COLOR: RGBA = [0, 255, 255, 100];
/** Fill for the selected (clicked) room. */
export const SELECTED_ROOM_HIGHLIGHT: RGBA = [0, 255, 255, 100];
/** Outline for the selected (clicked) room. */
export const SELECTED_ROOM_OUTLINE: RGBA = [0, 200, 200, 255];
/** Translucent fill for the hovered room (ESRI only). */
export const HOVER_ROOM_HIGHLIGHT: RGBA = [255, 255, 0, 60];

// ─── Call-floor highlight ───────────────────────────────────────────────────

/** Fallback outline color when the call is on the currently viewed floor. */
export const CALL_FLOOR_HIGHLIGHT_COLOR: RGBA = [255, 87, 34, 255];
/** Outline width (pixels) for the call-floor highlight. */
export const CALL_FLOOR_HIGHLIGHT_WIDTH = 4;

// ─── Outdoor points (outdoor_point MVT layer) ──────────────────────────────

/** The MVT layer name for outdoor points. */
export const OUTDOOR_POINT_LAYER_NAME = 'outdoor_point';
/** Light shadow so it doesn't gray out the label (draw order varies by map). */
export const OUTDOOR_LABEL_SHADOW: RGBA = [0, 0, 0, 55];
